import React, { CSSProperties, useEffect, useState } from "react";
import { UserService, Campaign } from "../../services/UserService";
import { ReportService, Report } from "../../services/ReportService";
import HeaderUser from "../../components/HeaderUser";


const fallbackReports: Report[] = [
  { id: -1, name: "Relatório de Cupons", slug: "relatorio_cupons" },
  { id: -2, name: "Relatório de Cadastros", slug: "relatorio_cadastros" },
];

const reportDescriptions: Record<string, string> = {
  relatorio_cupons: "Resumo diário do volume de cupons por campanha.",
  relatorio_cadastros: "Visão consolidada de cadastros e evolução por período.",
};

const Reports: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportNotice, setReportNotice] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleCampaignChange = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    localStorage.setItem("selectedCampaignId", campaign.id.toString());
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingUser(true);
      setLoadingReports(true);
      try {
        const userData = await UserService.getUserWithCamaigns();
        setUser(userData);
        setCampaigns(userData.campaigns ?? null);
        setError(null);

        if (userData.campaigns && userData.campaigns.length > 0) {
          const storedCampaignId = localStorage.getItem("selectedCampaignId");

          const selected = storedCampaignId
            ? userData.campaigns.find((c) => c.id === Number(storedCampaignId))
            : userData.campaigns[0];

          setSelectedCampaign(selected || userData.campaigns[0]);
        }
      } catch (err) {
        setError("Erro ao buscar campanhas do usuário.");
      } finally {
        setLoadingUser(false);
      }

      try {
        const reportList = await ReportService.getAllReports();
        setReports(reportList);
        setReportNotice(null);
      } catch (reportError) {
        console.error("Erro ao buscar relatórios:", reportError);
        setReports([]);
        setReportNotice("Não foi possível carregar os relatórios neste ambiente.");
      } finally {
        setLoadingReports(false);
      }
    };

    fetchData();
  }, []);

  const hasCampaigns = Boolean(campaigns && campaigns.length > 0);
  const reportsAvailable = reports.length > 0;
  const canDownload = reportsAvailable && !loadingReports;
  const displayReports = reportsAvailable ? reports : fallbackReports;
  const reportStatus = loadingReports ? "Carregando" : reportsAvailable ? "Disponível" : "Em breve";
  const emptyTitle = reportNotice
    ? "Relatórios indisponíveis no momento."
    : "Nenhum relatório publicado neste ambiente.";
  const emptySubtitle =
    reportNotice || "Assim que liberarmos, eles aparecem aqui automaticamente.";
  const statusLabel = reportsAvailable ? `${reports.length} disponíveis` : "Em evolução";
  const heroLabel = reportsAvailable ? "Disponível" : "Atualização";
  const heroTitle = reportsAvailable
    ? "Relatórios prontos para download"
    : "Relatórios mais objetivos, com leitura rápida";
  const heroDescription = reportsAvailable
    ? "Selecione um relatório e baixe o arquivo pronto para análise."
    : "Estamos redesenhando esta área para trazer comparativos diários e insights prontos para decisão.";
  const campaignLabel = selectedCampaign?.name || (hasCampaigns ? "Selecionar campanha" : "Sem campanhas");
  const campaignNotice =
    !loadingUser && (error || !user || !hasCampaigns)
      ? error || "Nenhuma campanha encontrada para este usuário."
      : null;
  const campaignNoticeVariant = error ? "error" : "warning";
  const campaignColorStyles = {
    "--primary-color": selectedCampaign?.primary_color ?? "#0b3d91",
    "--secondary-color": selectedCampaign?.secondary_color ?? "#1e68ff",
  } as CSSProperties;

  const handleDownload = async (reportId: number) => {
    if (!selectedCampaign) {
      alert("Selecione uma campanha primeiro.");
      return;
    }

    try {
      setDownloadingId(reportId);
      await ReportService.getReport(reportId, selectedCampaign.id);
    } catch (downloadError) {
      console.error("Erro no download do relatório:", downloadError);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="reports-page" style={campaignColorStyles}>
      <HeaderUser campaigns={campaigns} selectedCampaign={selectedCampaign} onSelectCampaign={handleCampaignChange} />

      <div className="reports-container">
        <div className="page-head">
          <div className="title-block">
            <p className="eyebrow">Relatórios</p>
            <h2>Relatórios da campanha</h2>
            <p>Escolha um relatório e faça download do arquivo pronto com base na campanha selecionada.</p>
          </div>
          <div className="status-chip">
            <span className="dot" />
            {statusLabel}
          </div>
        </div>

        {campaignNotice && <div className={`state-card ${campaignNoticeVariant}`}>{campaignNotice}</div>}

        <div className="hero-card">
          <div className="hero-text">
            <p className="hero-label">{heroLabel}</p>
            <h3>{heroTitle}</h3>
            <p>{heroDescription}</p>
          </div>
        </div>

        <div className="reports-grid">
          <div className="section-head">
            <div>
              <h3>Relatórios planejados</h3>
            </div>
          </div>

          <div className="report-list">
            {displayReports.map((report) => {
              const description =
                reportDescriptions[report.slug] || "Resumo consolidado da campanha selecionada.";
              const isDownloading = downloadingId === report.id;

              return (
                <article className={`report-card${loadingReports ? " is-loading" : ""}`} key={report.id}>
                  <div className="report-card-header">
                    <span className="report-chip">{reportStatus}</span>
                  </div>
                  <h4>{report.name}</h4>
                  <p className="report-desc">{description}</p>
                  <div className="report-meta">
                    <span>Campanha: {campaignLabel}</span>
                    <span>Formato: XLSX</span>
                  </div>
                  <div className="report-actions">
                    <button
                      className="report-button"
                      type="button"
                      disabled={!canDownload || isDownloading}
                      onClick={() => {
                        if (canDownload) {
                          handleDownload(report.id);
                        }
                      }}
                    >
                      {loadingReports
                        ? "Carregando..."
                        : canDownload
                          ? isDownloading
                            ? "Baixando..."
                            : "Baixar relatório"
                          : "Em breve"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {!loadingReports && reports.length === 0 && (
            <div className="empty-note">
              <strong>{emptyTitle}</strong>
              <span>{emptySubtitle}</span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Reports;
